import React from 'react';
import * as THREE from 'three';
import { invalidate } from '@react-three/fiber';
import { Model } from '../../hooks/useModelState';
import { cadToThreeJsPositionWithOffset } from '../../Scenes/scene-utils';
import { BULK_IMPORT_GHOST_NODE_ID } from '../../Scenes/BulkImportSolidsScene/bulkImportGhost';
import {
  getBulkImportSolidsSceneState,
  getCurrentSolid,
  subscribeToBulkImportSolidsSceneState
} from '../../Scenes/BulkImportSolidsScene/bulkImportSolidsSceneState';

interface BulkImportProjectionLineProps {
  model: Model;
  offset: THREE.Vector3;
  scale: number;
}

const BulkImportProjectionLine = ({ model, offset, scale }: BulkImportProjectionLineProps) => {
  const [, forceUpdate] = React.useReducer((x: number) => x + 1, 0);

  const ghost = model.nodes[BULK_IMPORT_GHOST_NODE_ID];

  // The node the decision records, not the current selection, which survives moving to the next solid.
  const { decisions } = getBulkImportSolidsSceneState();
  const currentSolid = getCurrentSolid();
  const sourceNodeId = currentSolid?.guid ? decisions[currentSolid.guid]?.spoolFromNodeId : undefined;
  const source = sourceNodeId ? model.nodes[sourceNodeId] : undefined;

  const lineObject = React.useMemo(() => {
    const geometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(),
      new THREE.Vector3(),
    ]);
    const material = new THREE.LineBasicMaterial({ color: 0xff6600 });
    return new THREE.Line(geometry, material);
  }, []);

  React.useEffect(() => () => {
    lineObject.geometry.dispose();
    (lineObject.material as THREE.Material).dispose();
  }, [lineObject]);

  React.useEffect(() => {
    const unsubscribe = subscribeToBulkImportSolidsSceneState(() => {
      forceUpdate();
      invalidate();
    });
    return unsubscribe;
  }, []);

  React.useEffect(() => {
    if (!ghost || !source) return;
    const [sx, sy, sz] = cadToThreeJsPositionWithOffset(source.coords, offset, scale);
    const [gx, gy, gz] = cadToThreeJsPositionWithOffset(ghost.coords, offset, scale);

    const positions = lineObject.geometry.attributes.position as THREE.BufferAttribute;
    positions.setXYZ(0, sx, sy, sz);
    positions.setXYZ(1, gx, gy, gz);
    positions.needsUpdate = true;
    lineObject.geometry.computeBoundingSphere();
  }, [ghost, source, offset, scale, lineObject]);

  if (!ghost || !source) return null;

  return <primitive object={lineObject} />;
};

export default BulkImportProjectionLine;
